// Updated api.ts to connect to FastAPI backend instead of Supabase

// CHANGED: Removed Supabase imports
// import { createClient } from '@supabase/supabase-js';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

interface TokenUsageRecord {
  website_id: string;
  service_type: string;
  tokens_used: number;
  cost_estimate: number;
  websites: {
    name: string;
    domain: string;
  };
}

interface ServiceBreakdownRecord {
  service_type: string;
  tokens_used: number;
  cost_estimate: number;
}

interface AggregatedUsage {
  website_id: string;
  website_name: string;
  domain: string;
  pipelines: {
    standard: { tokens: number; cost: number; calls: number };
    gemini_realtime: { tokens: number; cost: number; calls: number };
    openai_realtime: { tokens: number; cost: number; calls: number };
  };
  services: {
    google_llm: { tokens: number; cost: number; requests: number };
    openai_llm: { tokens: number; cost: number; requests: number };
    deepgram_stt: { tokens: number; cost: number; requests: number };
    cartesia_tts: { tokens: number; cost: number; requests: number };
    gemini_realtime: { tokens: number; cost: number; requests: number };
    openai_realtime: { tokens: number; cost: number; requests: number };
  };
  total_tokens: number;
  total_cost: number;
}

interface ServiceSummary {
  service_type: string;
  total_tokens: number;
  total_cost: number;
  request_count: number;
  avg_tokens_per_request: number;
  avg_cost_per_request: number;
}

// CHANGED: Removed Supabase client initialization
// const supabase = createClient(...);

// Helper function for API calls
async function apiCall(endpoint: string, options: RequestInit = {}) {
  const token = localStorage.getItem('access_token');
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

export const api = {
  async getWebsites() {
    try {
      console.log('Fetching websites from API...');
      const data = await apiCall('/websites');
      console.log('Raw data from API:', data);
      return data || [];
    } catch (error) {
      console.error('API Error:', error);
      return [];
    }
  },

  async createWebsite(website: any) {
    try {
      // Start with common fields
      const websiteData: any = {
        name: website.name,
        domain: website.domain,
        industry: website.industry,
        timezone: website.timezone,
        status: website.status,
        prompt: website.prompt,
        personality: website.personality,
        conversationStyle: website.conversationStyle,
        language: website.language,
        responseLength: website.responseLength,
        fallbackBehavior: website.fallbackBehavior,
        sources: website.sources || [],
        voiceSettings: website.voiceSettings,
        businessHours: website.businessHours,
        integrations: website.integrations,
        notificationEmail: website.notificationEmail || null,
        uiTemplate: website.uiTemplate || 'default',
        agentMode: website.agentMode || 'standard',
      };
  
      // Clear all pipeline-specific fields first
      websiteData.sttProvider = null;
      websiteData.sttModel = null;
      websiteData.llmProvider = null;
      websiteData.llmModel = null;
      websiteData.ttsProvider = null;
      websiteData.ttsModel = null;
      websiteData.ttsVoice = null;
      websiteData.realtimeModel = null;
      websiteData.realtimeVoice = null;
  
      // Set only relevant fields based on pipeline mode
      if (website.agentMode === 'openai_realtime') {
        websiteData.realtimeModel = website.realtimeModel || 'gpt-realtime-mini';
        websiteData.realtimeVoice = website.realtimeVoice || 'shimmer';
      } 
      else if (website.agentMode === 'gemini_realtime') {
        websiteData.realtimeModel = website.realtimeModel || 'gemini-2.5-flash-exp';
        websiteData.realtimeVoice = website.realtimeVoice || 'Puck';
      }
      else if (website.agentMode === 'standard') {
        websiteData.sttProvider = website.sttProvider || 'deepgram';
        websiteData.sttModel = website.sttModel || 'nova-3';
        websiteData.llmProvider = website.llmProvider || 'google';
        websiteData.llmModel = website.llmModel || 'gemini-2.0-flash';
        websiteData.ttsProvider = website.ttsProvider || 'cartesia';
        websiteData.ttsModel = website.ttsModel || 'sonic-3';
        websiteData.ttsVoice = website.ttsVoice || 'f31cc6a7-c1e8-4764-980c-60a361443dd1'; // Aus Lady
      }
  
      console.log('Creating website with data:', websiteData);
  
      const data = await apiCall('/websites', {
        method: 'POST',
        body: JSON.stringify(websiteData),
      });
  
      return data;
    } catch (error) {
      console.error('Create website error:', error);
      throw error;
    }
  },

  async updateWebsite(id: string, website: any) {
    try {
      // Start with common fields
      const updateData: any = {
        name: website.name,
        domain: website.domain,
        industry: website.industry,
        timezone: website.timezone,
        status: website.status,
        prompt: website.prompt,
        personality: website.personality,
        conversationStyle: website.conversationStyle,
        language: website.language,
        responseLength: website.responseLength,
        fallbackBehavior: website.fallbackBehavior,
        sources: website.sources || [],
        voiceSettings: website.voiceSettings,
        businessHours: website.businessHours,
        integrations: website.integrations,
        notificationEmail: website.notificationEmail || null,
        uiTemplate: website.uiTemplate || 'default',
        agentMode: website.agentMode || 'standard',
      };
  
      // Clear all pipeline-specific fields first
      updateData.sttProvider = null;
      updateData.sttModel = null;
      updateData.llmProvider = null;
      updateData.llmModel = null;
      updateData.ttsProvider = null;
      updateData.ttsModel = null;
      updateData.ttsVoice = null;
      updateData.realtimeModel = null;
      updateData.realtimeVoice = null;
  
      // Set only relevant fields based on pipeline mode
      if (website.agentMode === 'openai_realtime') {
        updateData.realtimeModel = website.realtimeModel || 'gpt-realtime-mini';
        updateData.realtimeVoice = website.realtimeVoice || 'shimmer';
      } 
      else if (website.agentMode === 'gemini_realtime') {
        updateData.realtimeModel = website.realtimeModel || 'gemini-2.5-flash-exp';
        updateData.realtimeVoice = website.realtimeVoice || 'Puck';
      }
      else if (website.agentMode === 'standard') {
        updateData.sttProvider = website.sttProvider || 'deepgram';
        updateData.sttModel = website.sttModel || 'nova-3';
        updateData.llmProvider = website.llmProvider || 'google';
        updateData.llmModel = website.llmModel || 'gemini-2.0-flash';
        updateData.ttsProvider = website.ttsProvider || 'cartesia';
        updateData.ttsModel = website.ttsModel || 'sonic-3';
        updateData.ttsVoice = website.ttsVoice || 'f31cc6a7-c1e8-4764-980c-60a361443dd1'; // Aus Lady
      }
  
      console.log('Updating website with data:', updateData);
  
      const data = await apiCall(`/websites/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });
  
      return data;
    } catch (error) {
      console.error('Update website error:', error);
      throw error;
    }
  },

  async deleteWebsite(id: string) {
    try {
      await apiCall(`/websites/${id}`, {
        method: 'DELETE',
      });
      return { success: true };
    } catch (error) {
      console.error('Delete website error:', error);
      throw error;
    }
  },

  async getConversations() {
    try {
      console.log('Fetching conversations from API...');
      const data = await apiCall('/conversations');
      console.log('Conversations data:', data);
      return data || [];
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return [];
    }
  },

  async getWebsiteSessionCount(websiteId: string) {
    try {
      const data = await apiCall(`/websites/${websiteId}/session-count`);
      return data.count || 0;
    } catch (error) {
      console.error('Error getting session count:', error);
      return 0;
    }
  },

  async getConversationsBySession(userSessionId: string) {
    try {
      console.log('Fetching conversations for session:', userSessionId);
      const data = await apiCall(`/conversations/session/${userSessionId}`);
      return data || [];
    } catch (error) {
      console.error('Error fetching conversations by session:', error);
      return [];
    }
  },

  // Token usage tracking methods
  async getTokenUsage(websiteId?: string, daysBack: number = 30): Promise<AggregatedUsage[]> {
    try {
      const params = new URLSearchParams();
      if (websiteId) params.append('website_id', websiteId);
      params.append('days_back', daysBack.toString());

      const data = await apiCall(`/token-usage?${params.toString()}`);
      return data || [];
    } catch (error) {
      console.error('Get token usage error:', error);
      return [];
    }
  },

  async getServiceBreakdown(websiteId?: string, daysBack: number = 30): Promise<ServiceSummary[]> {
    try {
      const params = new URLSearchParams();
      if (websiteId) params.append('website_id', websiteId);
      params.append('days_back', daysBack.toString());

      const data = await apiCall(`/token-usage/breakdown?${params.toString()}`);
      return data || [];
    } catch (error) {
      console.error('Get service breakdown error:', error);
      return [];
    }
  },

  // NEW: Get call history
  async getCallHistory(websiteId?: string, daysBack: number = 30): Promise<any[]> {
    try {
      const params = new URLSearchParams();
      if (websiteId && websiteId !== 'all') params.append('website_id', websiteId);
      params.append('days_back', daysBack.toString());

      const data = await apiCall(`/call-history?${params.toString()}`);
      return data || [];
    } catch (error) {
      console.error('Get call history error:', error);
      return [];
    }
  },

  // NEW: Get pipeline comparison
  async getPipelineComparison(websiteId?: string, daysBack: number = 30): Promise<any[]> {
    try {
      const params = new URLSearchParams();
      if (websiteId && websiteId !== 'all') params.append('website_id', websiteId);
      params.append('days_back', daysBack.toString());

      const data = await apiCall(`/pipeline-comparison?${params.toString()}`);
      return data || [];
    } catch (error) {
      console.error('Get pipeline comparison error:', error);
      return [];
    }
  },

  // Lead Management methods (placeholder - not implemented in backend yet)
  async getConversationAnalyses() {
    console.warn('getConversationAnalyses not implemented in SQLite backend yet');
    return [];
  },

  async markLeadAsFollowedUp(analysisId: string) {
    console.warn('markLeadAsFollowedUp not implemented in SQLite backend yet');
    return { success: true };
  },

  // Note: Conversation analysis methods removed as they're not in the SQLite schema
  // If you need these, you'll need to add the tables to database.py and endpoints to api_server.py
};